import { Injectable, inject } from '@angular/core';
import { StorageEngine } from './storage-engine.interface';
import { BrowserStorageService } from './browser-storage.service';
import { ServerStorageService } from './server-storage.service';
import { SessionStorageService } from './session-storage.service';
import { PlatformService } from '../platform.service';

@Injectable({ providedIn: 'root' })
export class StorageService implements StorageEngine {
  private readonly platformService = inject(PlatformService);
  private readonly browser = inject(BrowserStorageService);
  private readonly server = inject(ServerStorageService);
  private readonly session = inject(SessionStorageService);

  /** Persistent store — survives a browser restart. */
  private readonly engine: StorageEngine = this.platformService.isBrowser()
    ? this.browser
    : this.server;

  /** Tab-scoped store, used when the user declines "remember me". */
  private readonly sessionEngine: StorageEngine = this.platformService.isBrowser()
    ? this.session
    : this.server;

  /** Reads the persistent store first, then the tab-scoped one. */
  getItem(key: string): string | null {
    return this.engine.getItem(key) ?? this.sessionEngine.getItem(key);
  }

  /**
   * @param session Write to tab-scoped storage instead of persistent storage.
   *   The key is removed from the other store so a value can never be stale in
   *   one while current in the other.
   */
  setItem(key: string, value: string, session = false): void {
    if (session) {
      this.engine.removeItem(key);
      this.sessionEngine.setItem(key, value);
      return;
    }
    this.sessionEngine.removeItem(key);
    this.engine.setItem(key, value);
  }

  /** Removes from both stores. */
  removeItem(key: string): void {
    this.engine.removeItem(key);
    this.sessionEngine.removeItem(key);
  }

  clear(): void {
    this.engine.clear();
    this.sessionEngine.clear();
  }

  /** True when the key lives only in tab-scoped storage. */
  isSessionScoped(key: string): boolean {
    return this.engine.getItem(key) === null && this.sessionEngine.getItem(key) !== null;
  }

  /**
   * Set user's notification decision
   * @param userId - User ID to associate the decision with
   * @param decision - User's decision about notifications ('allowed' | 'denied')
   */
  setUserNotificationDecision(userId: string, decision: 'allowed' | 'denied'): void {
    const key = 'notificationDecision';
    const existingData = this.getItem(key);
    let notificationDecisions: Record<string, 'allowed' | 'denied'> = {};

    if (existingData) {
      try {
        notificationDecisions = JSON.parse(existingData);
      } catch (error) {
        console.warn('Error parsing existing notification decisions:', error);
        notificationDecisions = {};
      }
    }

    notificationDecisions[userId] = decision;
    this.setItem(key, JSON.stringify(notificationDecisions));
  }

  /**
   * Get user's notification decision
   * @param userId - User ID to get the decision for
   * @returns User's notification decision or null if not set
   */
  getUserNotificationDecision(userId: string): 'allowed' | 'denied' | null {
    const key = 'notificationDecision';
    const data = this.getItem(key);

    if (!data) {
      return null;
    }

    try {
      const notificationDecisions: Record<string, 'allowed' | 'denied'> = JSON.parse(data);
      return notificationDecisions[userId] || null;
    } catch (error) {
      console.warn('Error parsing notification decisions:', error);
      return null;
    }
  }

  /**
   * Remove user's notification decision
   * @param userId - User ID to remove the decision for
   */
  removeUserNotificationDecision(userId: string): void {
    const key = 'notificationDecision';
    const data = this.getItem(key);

    if (!data) {
      return;
    }

    try {
      const notificationDecisions: Record<string, 'allowed' | 'denied'> = JSON.parse(data);
      delete notificationDecisions[userId];

      if (Object.keys(notificationDecisions).length === 0) {
        this.removeItem(key);
      } else {
        this.setItem(key, JSON.stringify(notificationDecisions));
      }
    } catch (error) {
      console.warn('Error parsing notification decisions for removal:', error);
    }
  }
}
